import { GLServiceBase, ServiceRoute, GLUtil, getServiceMethodKey, HttpMethod, ServerExceptionCode, fmtMsg } from "commonui";
import { IContentService, ContentForEditModel } from ".";
import {
	EditContentRequestModel,
	CreateContentRequestModel,
	RelatedContentResponseModel,
	CompareContentModel,
	PossibleChapterModel,
	CreateChapterRequestModel,
	ContentInfoModel,
	ContentCMImageResponseModel,
	ContentCMImage,
	ContentTitleModel
} from "./model";
import { v4 } from "uuid";
import { TypeResponseModel } from "../category-manager";
import { ContentSimpleModel } from "../resource";
import { ContentMediaSlotStatusInfoModel, ContentMediaSlotUploadInfoModel, HasPdfContentResponseModel, PageAndAudioMediaResponseModel } from "../media-uploader";
import { CommonLocale } from "@/locales/localeid";

class ContentServiceClass extends GLServiceBase<any, any> implements IContentService {
	serviceRoute: ServiceRoute = { prefix: "content/v1/VcsContent" };

	errMsgs = (() => {
		const result = {};
		result[
			getServiceMethodKey(this, HttpMethod.Get, ServerExceptionCode.ContentNotExistException)
		] = fmtMsg(CommonLocale.CommonContentNotExist);

		result[
			getServiceMethodKey(this, HttpMethod.Get, ServerExceptionCode.NoSpecialDesignationException)
		] = fmtMsg(CommonLocale.CommonSpecialDesignationNotExist);

		return result;
	})();

	getContentWithText(
		contentId: string,
		revision: number,
		grapeSeedVersion?: string,
		littleSeedVersion?: string
	): Promise<ContentForEditModel> {
		return this.get("getContentForEdit", { contentId, revision, grapeSeedVersion, littleSeedVersion });
	}

	duplicate(contentId: string, versionId: string): Promise<string> {
		const path = "duplicate?" + GLUtil.queryStringify({ contentId, versionId });
		return this.create(path);
	}

	editContent(
		model: EditContentRequestModel,
		editedRevision: number,
		createRevision: boolean,
		versionId: string
	): Promise<boolean> {
		return this.update(
			"content?" + GLUtil.queryStringify({ editedRevision, createRevision, versionId }),
			model,
			undefined
		);
	}

	createContent(model: CreateContentRequestModel, versionId: string): Promise<string> {
		return this.create("{versionId}", model, { versionId });
	}

	calculateWords(contentIds: string[], versionId, vcsLevelId) {
		return this.get("calculateWords", { contentIds, versionId, vcsLevelId });
	}

	getRelatedContent(contentId): Promise<RelatedContentResponseModel[]> {
		return this.get("relatedContent/{contentId}", { contentId });
	}

	getContent(contentId: string[]): Promise<CompareContentModel[]> {
		return this.request.get(
			this.url(""),
			contentId.map((c) => ({ contentId: c }))
		);
	}

	checkNewWords(data: string[]): Promise<string[]> {
		return this.create("CheckNewWords", data);
	}

	getPossibleChildren(levelId: number, versionId: string): Promise<PossibleChapterModel[]> {
		return this.get("{versionId}/chapter/{levelId}/possibleChildren", { versionId, levelId });
	}

	createChapter(model: CreateChapterRequestModel): Promise<ContentInfoModel> {
		return this.create("chapter", model, undefined);
	}

	linkChapter(contentId: string, chapterId: string, sequence: number): Promise<ContentInfoModel> {
		return this.create("{contentId}/link/{chapterId}/{sequence}", { contentId, chapterId, sequence });
	}

	unlinkChapter(chapterId: string): Promise<void> {
		return this.delete("unlink/{chapterId}", { chapterId });
	}

	updateSequence(parentId: string, chapterId: string, sequence: number): Promise<any[]> {
		return this.update("{parentId}/updateSequence/{chapterId}/{sequence}", { parentId, chapterId, sequence });
	}

	UpdateContentParentType(contentId: string, type: number): Promise<void> {
		return this.update("{contentId}/updateParentType/{type}", { contentId, type });
	}

	getImages(
		versionGroupId: string,
		keyword: string,
		unitId: string,
		contentIds: string[] | null,
		categoryId?: number
	) {
		return this.get<ContentCMImageResponseModel[]>("images", {
			versionGroupId,
			keyword,
			unitId,
			contentIds,
			categoryId
		}).then((res) => {
			const unGroupedImages: ContentCMImage[] = [];
			res.forEach((item) => {
				const { imageUrls, imageUrlDetails, ...rest } = item;
				imageUrls.forEach((imageUrl, index) => {
					const imageDetail = imageUrlDetails.find((detail) => detail.url === imageUrl)!;
					unGroupedImages.push({ ...rest, imageUrl, id: v4(), imageDetail: imageDetail });
				});
			});
			return unGroupedImages;
		});
	}

	getPageImage(pageId: string): Promise<ContentCMImage> {
		return this.get<ContentCMImageResponseModel>("pageImage/{pageId}", { pageId }).then((res) => {
			let data: ContentCMImage = {
				id: "",
				unit: 0,
				unitId: 0,
				contentId: "",
				contentTitle: "",
				imageUrl: "",
				imageDetail: { pageId: "", url: "" },
				mediaId: ""
			};
			const { imageUrls, imageUrlDetails, ...rest } = res;
			imageUrls.forEach((imageUrl, index) => {
				const imageDetail = imageUrlDetails.find((detail) => detail.url === imageUrl)!;
				data = { ...rest, id: v4(), imageUrl, imageDetail };
			});

			return data;
		});
	}

	getCMTitles(versionGroupId: string) {
		return this.get<ContentTitleModel[]>("CMTitles", { versionGroupId });
	}

	isChapterPresentInExposure(chapterId: string, parentId: string, hasReader: boolean): Promise<boolean> {
		return this.get("isChapterPresentInExposure/{chapterId}/{parentId}", { chapterId, parentId, hasReader });
	}

	ContentScheduled(contentId: string): Promise<any> {
		return this.get("ContentScheduled/{contentId}", { contentId });
	}

	GetMediaSlots(contentId: string, mediaSlotType: number): Promise<ContentMediaSlotUploadInfoModel> {
		return this.get("MediaSlots/{contentId}", { contentId, mediaSlotType });
	}

	getLessonRelatedItemSubCategories(specialDesignationId?: number): Promise<TypeResponseModel[]> {
		return this.get("lessonrelateditem/subcategories", { specialDesignationId });
	}

	getLessonRelatedItemComponents(params: {
		versionGroupId: string;
		subCategoryId: number;
		unitId: number;
		contentId?: string | null;
		specialDesignationId?: number;
	}): Promise<{ data: ContentSimpleModel[]; notMatchedData: ContentSimpleModel[]; }> {
		return this.get(
			"lessonrelateditem/versiongroup/{versionGroupId}/subcategory/{subCategoryId}/unit/{unitId}",
			params
		);
	}

	ConversationRelatedLesson(contentId: string): Promise<boolean> {
		return this.get("HasUsedConversation", { contentId });
	}

	HasPDFContents(contentId: string, pdfSlotTypes: number[]): Promise<HasPdfContentResponseModel[]> {
		return this.get("HasPDFContent/{contentId}", {contentId, pdfSlotTypes})
	}

	GetMediaSlotStatus(contentId: string, mediaSlotType: number[]): Promise<ContentMediaSlotStatusInfoModel> {
		return this.get("MediaSlotStatus/{contentId}", { contentId, mediaSlotType });
	}

	GetIndividualAudio(contentId: string, mediaSlot: number): Promise<PageAndAudioMediaResponseModel> {
		return this.get("IndividualAudioMedia/{contentId}", { contentId, mediaSlot });
	}
}

export const ContentService = new ContentServiceClass();
